import { useEffect, useMemo, useState } from "react";
import { Select, Table } from "antd";
import axios from "axios";
import { EnvironmentOutlined, WalletOutlined } from "@ant-design/icons";
import { CREATE_jwel } from "../../Config/Config";
import PdfExcelPrint from "../Utilities/PdfExcelPrint";

// Shared Inventory-style components — same ones used by BankStatement.jsx
// and the other converted report screens. Adjust the path if your
// project's shared folder differs.
import styles from "../Pages/shared/Inventory.module.css";
import InventoryPageHeader from "../Pages/shared/InventoryPageHeader";
import InventoryTable from "../Pages/shared/InventoryTable";

const { Option } = Select;

/* ─── filter box theme palette (same as the other report screens) ─── */
const FILTER_THEMES = {
  purple: {
    text: "#7c3aed",
    bg: "#ffffff",
    border: "#c4b5fd",
    ring: "rgba(124,58,237,0.15)",
  },
};

const FilterField = ({ icon, label, theme = "purple", width, children }) => {
  const c = FILTER_THEMES[theme];
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        background: c.bg,
        border: `2px solid ${c.border}`,
        borderRadius: 12,
        padding: "7px 10px 9px",
        minHeight: 64,
        width,
        boxShadow: `0 1px 3px ${c.ring}`,
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
      }}
    >
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12,
          fontWeight: 700,
          color: c.text,
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        {icon} {label}
      </span>
      <div
        style={{
          background: "#fff",
          border: "1.5px solid #d6dbe3",
          borderRadius: 8,
          padding: "1px 8px",
          display: "flex",
          alignItems: "center",
        }}
      >
        {children}
      </div>
    </div>
  );
};

/* ---------------- Shared card-view field maps (mobile) ---------------- */
const primaryFields = [
  { key: "PARTYNAME", label: "Party Name" },
  { key: "AREA", label: "Area" },
];
const secondaryFields = [{ key: "PAYAMT", label: "Payment" }];

const TotalDues = () => {
  const [rawData, setRawData] = useState([]);
  const [areaDropDown, setAreaDropDown] = useState([]);
  const [selectArea, setSelectArea] = useState();
  const [loading, setLoading] = useState(false);
  const [tableVisible, setTableVisible] = useState(false);
  const tenantName = localStorage.getItem("tenantName");

  /* ---- data / API logic (unchanged endpoints + query params/format) ---- */
  useEffect(() => {
    axios
      .get(`${CREATE_jwel}/api/ERP/GetOutStandingCustForCity`, {
        headers: {
          tenantName: tenantName,
        },
      })
      .then((response) => {
        const data = response.data;
        if (Array.isArray(data) && data.length > 0) {
          setAreaDropDown(data);
        }
      })
      .catch((error) => {
        console.error("Error fetching area dropdown:", error);
      });
  }, [tenantName]);

  useEffect(() => {
    setLoading(true);
    setTableVisible(false);

    axios
      .get(
        `${CREATE_jwel}/api/ERP/GetOutstandingCustemerData?area=${selectArea ? selectArea : "%"
        }`,
        {
          headers: {
            tenantName: tenantName,
          },
        },
      )
      .then((response) => {
        const detailsData = response.data;
        const calculatedData = detailsData.map((item, index) => ({
          ...item,
          key: index + 1,
          serialNo: index + 1,
        }));
        setRawData(calculatedData);
        setTimeout(() => setTableVisible(true), 80);
      })
      .catch((error) => {
        console.error("Error fetching total dues details:", error);
      })
      .finally(() => setLoading(false));
  }, [selectArea, tenantName]);

  const columns = [
    {
      title: "S.No",
      dataIndex: "serialNo",
      width: 50,
      align: "center",
      key: "serialNo",
      className: "col-sno",
    },
    { title: "Area", dataIndex: "AREA", key: "AREA" },
    {
      title: "Party Name",
      dataIndex: "PARTYNAME",
      key: "PARTYNAME",
      render: (value) => <span className="tagBadge">{value}</span>,
    },
    {
      title: "Payment",
      dataIndex: "PAYAMT",
      key: "PAYAMT",
      align: "right",
      render: (value) => (
        <strong style={{ color: value < 0 ? "#ef4444" : "#0f766e" }}>
          {Number(value || 0).toFixed(2)}
        </strong>
      ),
    },
  ];

  const totals = useMemo(() => {
    return rawData.reduce(
      (acc, item) => {
        acc.payment += Number(item.PAYAMT || 0);
        return acc;
      },
      { payment: 0 },
    );
  }, [rawData]);

  const formattedData = useMemo(
    () =>
      rawData.map((item, index) => ({
        ...item,
        serialNo: index + 1,
        PAYAMT: Number(item.PAYAMT || 0),
      })),
    [rawData],
  );

  const exportData = [
    ...formattedData.map((item) => ({
      ...item,
      PAYAMT: item.PAYAMT.toFixed(2),
    })),
    {
      serialNo: "Total",
      AREA: "",
      PARTYNAME: "",
      PAYAMT: totals.payment.toFixed(2),
    },
  ];

  return (
    <div className={styles.page}>
      <style>{`
        .ant-table-thead > tr > th {
          white-space: nowrap !important;
        }
      `}</style>

      <InventoryPageHeader
        title="Today Dues"
        subtitle="CRM · Today Dues"
        icon={<WalletOutlined style={{ color: "#fff", fontSize: 16 }} />}
        recordCount={rawData.length}
        visible={rawData.length > 0}
        stats={[
          { label: "Records", value: rawData.length, color: "blue" },
          {
            label: "Payment",
            value: totals.payment.toFixed(2),
            color: "teal",
          },
        ]}
      />

      <div
        className={styles.toolbar}
        style={{ flexWrap: "wrap", gap: 10, alignItems: "stretch" }}
      >
        <div
          className={styles.toolbarLeft}
          style={{ flexWrap: "wrap", gap: 10, alignItems: "stretch" }}
        >
          <FilterField
            icon={<EnvironmentOutlined />}
            label="Select Area"
            theme="purple"
          >
            <Select
              allowClear
              showSearch
              placeholder="Select Area"
              size="middle"
              bordered={false}
              style={{ width: 200 }}
              value={selectArea || undefined}
              onChange={(value) => setSelectArea(value)}
              filterOption={(input, option) =>
                (option?.children || "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {areaDropDown.map((area, index) => (
                <Option key={index} value={area.AREA}>
                  {area.AREA}
                </Option>
              ))}
            </Select>
          </FilterField>
        </div>

        <div className={styles.toolbarRight}>
          <PdfExcelPrint
            data={exportData}
            columns={columns}
            fileName="TotalDues"
            disabled={formattedData.length === 0}
          />
        </div>
      </div>

      <InventoryTable
        columns={columns}
        dataSource={formattedData}
        rowKey="serialNo"
        tableClassName="dues-table"
        tableEnter={tableVisible}
        loading={loading}
        emptyText="No dues found for the selected area"
        primaryFields={primaryFields}
        secondaryFields={secondaryFields}
        summary={() => (
          <Table.Summary.Row
            style={{ background: "#f5f5f5", fontWeight: 600 }}
          >
            <Table.Summary.Cell>Total</Table.Summary.Cell>
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell align="right">
              {totals.payment.toFixed(2)}
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
    </div>
  );
};

export default TotalDues;